/* =========================================================
   Session, update window, per-screen variants, toasts.
   Toasts are always replicated into the bell queue (with the
   originating section), which is what the notification centre
   reads back.
   ========================================================= */
import { create } from 'zustand';
import type { Role, SessionUser, Toast, ToastInput, WindowState } from '@/types';
import { readJSON, readString, remove, writeJSON, writeString } from '@/lib/storage';
import { endSession } from '@/lib/session';

const K_USER = 'etc.user';
const K_ROLE = 'etc.role';

const DEFAULT_WINDOW: WindowState = { open: true, deadline: '2026-06-28T23:59:59' };

/** Per-screen layout variants.
 *  Site Manager cannot use view A on the dashboard; the Site and Org
 *  panels always open in their tabbed view. */
export interface VariantMap {
  dashboard: string;
  org: string;
  site: string;
  reports: string;
}

function initialVariant(role: Role): VariantMap {
  return role === 'SITE_MGR'
    ? { dashboard: 'B', org: 'C', site: 'A', reports: 'A' }
    : { dashboard: 'A', org: 'C', site: 'A', reports: 'A' };
}

/** Section label used as the origin of a notification raised on this screen. */
interface AppState {
  user: SessionUser | null;
  role: Role;
  loggingIn: boolean;
  loggingOut: boolean;
  windowState: WindowState;
  variant: VariantMap;
  toasts: Toast[];
  /** Bumped whenever the bell queue changes, so subscribers re-render. */
  notifVersion: number;
  /** Sube cada vez que llega del servidor un porcentaje guardado nuevo: la
   *  app se vuelve a dibujar con el dato de la base. */
  progresoVersion: number;
  bumpProgreso: () => void;
  /** Current screen id, used to stamp a notification's origin. */
  screen: string;
  /** Language, mirrored from i18n so non-React callers can read it. */
  lang: 'es' | 'en';

  setScreen: (screen: string) => void;
  setLang: (lang: 'es' | 'en') => void;
  beginLogin: (u: SessionUser, done: () => void) => void;
  beginLogout: (done: () => void) => void;
  /** Cierra la sesión sin la pantalla de transición: la usa el arranque
   *  cuando la sesión guardada ya no es válida. */
  dropSession: () => void;
  /** Aplica la identidad que acaba de confirmar el servidor tras una
   *  recarga. Sustituye a lo guardado en el navegador, que es una copia
   *  editable por su dueño. */
  adoptSession: (u: SessionUser) => void;
  setWindowState: (w: WindowState) => void;
  setVariantFor: (key: keyof VariantMap, v: string) => void;
  /** Devuelve el id del aviso, para poder retirarlo antes de tiempo. */
  addToast: (t: ToastInput) => string;
  dismissToast: (id: string) => void;
  /** Retira los avisos de éxito que siguen en pantalla. Se usa cuando el
   *  servidor rechaza un guardado que la interfaz ya había dado por bueno:
   *  dejar «Cambios guardados» al lado de «No se guardó» es peor que no
   *  avisar de nada. Los errores se conservan. */
  retractSuccessToasts: () => void;
  bumpNotifications: () => void;
}

/** Login and logout both run behind a 4s branded loading screen. */
const TRANSITION_MS = 4000;

export const useAppStore = create<AppState>((set) => ({
  user: readJSON<SessionUser | null>(K_USER, null),
  role: ((): Role => {
    const stored = readString(K_ROLE) as Role | null;
    if (stored === 'ETC_ADMIN' || stored === 'PARTNER_MGR' || stored === 'SITE_MGR') return stored;
    const u = readJSON<SessionUser | null>(K_USER, null);
    return u ? u.role : 'PARTNER_MGR';
  })(),
  loggingIn: false,
  loggingOut: false,
  // Valor de relleno: la hidratación lo sustituye por el de la base antes
  // de pintar nada.
  windowState: DEFAULT_WINDOW,
  variant: initialVariant('PARTNER_MGR'),
  toasts: [],
  notifVersion: 0,
  progresoVersion: 0,
  screen: 'dashboard',
  lang: 'es',

  setScreen: (screen) => set({ screen }),
  setLang: (lang) => set({ lang }),

  beginLogin: (u, done) => {
    set({ loggingIn: true });
    window.setTimeout(() => {
      writeJSON(K_USER, u);
      writeString(K_ROLE, u.role);
      set({ user: u, role: u.role, loggingIn: false, variant: initialVariant(u.role) });
      done();
    }, TRANSITION_MS);
  },

  beginLogout: (done) => {
    set({ loggingOut: true });
    // con backend real, además se invalida la sesión en el servidor
    void endSession();
    window.setTimeout(() => {
      remove(K_USER);
      set({ user: null, loggingOut: false });
      done();
    }, TRANSITION_MS);
  },

  dropSession: () => {
    remove(K_USER);
    set({ user: null, loggingIn: false, loggingOut: false });
  },

  adoptSession: (u) => {
    writeJSON(K_USER, u);
    writeString(K_ROLE, u.role);
    set({ user: u, role: u.role, variant: initialVariant(u.role) });
  },

  setWindowState: (w) => {
    // La ventana la manda la base, no el navegador. Guardar aquí una copia
    // solo serviría para que una versión vieja parpadeara al recargar, o
    // peor, para que dos administradores vieran cosas distintas.
    set({ windowState: w });
  },

  setVariantFor: (key, v) => set((s) => ({ variant: { ...s.variant, [key]: v } })),

  addToast: (t) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { id, ...t }] }));

    // La campana son los avisos que manda OTRA persona y los trae la API.
    // La maqueta metía aquí un eco de lo que uno mismo acababa de hacer,
    // porque no tenía nada más que enseñar; con servidor detrás eso los
    // mezclaría, y además desaparecería al recargar.
    set((s) => ({ notifVersion: s.notifVersion + 1 }));
    // Los persistentes esperan a que los cierren: son los que hay que
    // leer, no los que solo confirman que algo pasó.
    if (!t.persistente) {
      window.setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
      }, 4000);
    }
    return id;
  },

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  retractSuccessToasts: () =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.kind === 'err') })),

  bumpNotifications: () => set((s) => ({ notifVersion: s.notifVersion + 1 })),
  bumpProgreso: () => set((s) => ({ progresoVersion: s.progresoVersion + 1 })),
}));

/** Non-React access, for helpers that raise toasts outside a component. */
export const addToast = (t: ToastInput) => useAppStore.getState().addToast(t);
